import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box, Card, Grid, Typography, Avatar, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Button,
  TextField, Chip, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, Tooltip
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";
import StarHalfIcon from "@material-ui/icons/StarHalf";
import EmojiEventsIcon from "@material-ui/icons/EmojiEvents";
import PeopleIcon from "@material-ui/icons/People";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import GetAppIcon from "@material-ui/icons/GetApp";
import RefreshIcon from "@material-ui/icons/Refresh";
import { format, subDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  ResponsiveContainer, Cell
} from "recharts";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  root: { padding: "24px", backgroundColor: "#f0f2f5", minHeight: "100vh", [theme.breakpoints.down("sm")]: { padding: "12px 8px" } },
  header: { marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 12 },
  headerButtons: { display: "flex", gap: 8, flexShrink: 0, [theme.breakpoints.down("xs")]: { width: "100%" } },
  title: { fontSize: 26, fontWeight: 800, color: "#1a202c", letterSpacing: "-0.5px", [theme.breakpoints.down("sm")]: { fontSize: 20 } },
  subtitle: { fontSize: 14, color: "#718096", marginTop: 2 },
  filterCard: {
    backgroundColor: "#fff", borderRadius: 14, padding: "16px 20px",
    marginBottom: 24, boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap"
  },
  metricCard: {
    borderRadius: 14, height: "100%", overflow: "hidden",
    boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
    transition: "transform 0.2s, box-shadow 0.2s",
    "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 20px rgba(0,0,0,0.12)" }
  },
  metricInner: { padding: "20px 20px 16px", height: "100%" },
  metricLabel: { fontSize: 12, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px", marginBottom: 8 },
  metricValue: { fontSize: 36, fontWeight: 800, lineHeight: 1, marginBottom: 4 },
  metricSub: { fontSize: 12, marginTop: 4, opacity: 0.85 },
  sectionLabel: {
    fontSize: 13, fontWeight: 700, color: "#4a5568", textTransform: "uppercase",
    letterSpacing: "0.8px", marginBottom: 12, marginTop: 24
  },
  chartCard: { borderRadius: 14, backgroundColor: "#fff", padding: 20, boxShadow: "0 2px 8px rgba(0,0,0,0.07)" },
  chartTitle: { fontSize: 15, fontWeight: 700, color: "#2d3748", marginBottom: 16 },
  tableCard: { borderRadius: 14, backgroundColor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.07)", overflow: "hidden" },
  tableHead: { backgroundColor: theme.palette.primary.main },
  tableHeadCell: { color: "#fff !important", fontWeight: "700 !important", fontSize: "12px !important", textTransform: "uppercase", letterSpacing: "0.5px" },
  tableRow: {
    "&:hover": { backgroundColor: "#fff8f3" },
    "&:nth-of-type(even)": { backgroundColor: "#fafafa" }
  },
  avatar: { width: 38, height: 38, fontWeight: 700, fontSize: 15 },
  rankBadge: {
    width: 28, height: 28, borderRadius: "50%", display: "flex", alignItems: "center",
    justifyContent: "center", fontWeight: 800, fontSize: 13
  },
}));

const PERIOD_OPTIONS = [
  { label: "Hoje", value: 0 },
  { label: "Últimos 7 dias", value: 7 },
  { label: "Últimos 15 dias", value: 15 },
  { label: "Últimos 30 dias", value: 30 },
  { label: "Últimos 60 dias", value: 60 },
  { label: "Personalizado", value: "custom" },
];

const fmt = (d) => format(new Date(d), "yyyy-MM-dd");
const fmtDisplay = (d) => format(new Date(d + "T00:00:00"), "dd/MM/yyyy", { locale: ptBR });

const COLORS = ["#ed8936", "#a0aec0", "#c6a32b", "#4299e1", "#38a169", "#9f7aea", "#e53e3e", "#667eea"];

const StarRating = ({ rating }) => {
  const stars = [];
  for (let i = 1; i <= 5; i++) {
    if (i <= Math.floor(rating)) stars.push(<StarIcon key={i} style={{ color: "#f6c90e", fontSize: 16 }} />);
    else if (i === Math.ceil(rating) && rating % 1 >= 0.5) stars.push(<StarHalfIcon key={i} style={{ color: "#f6c90e", fontSize: 16 }} />);
    else stars.push(<StarBorderIcon key={i} style={{ color: "#cbd5e0", fontSize: 16 }} />);
  }
  return <Box display="flex" alignItems="center">{stars}</Box>;
};

const CustomBarTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  return (
    <Box style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 14px", boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
      <Typography style={{ fontSize: 13, fontWeight: 700, color: "#2d3748" }}>{payload[0]?.payload?.name}</Typography>
      <Typography style={{ fontSize: 20, fontWeight: 800, color: "#ed8936" }}>{payload[0]?.value} tickets</Typography>
    </Box>
  );
};

export default function RelatorioAtendentes() {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [period, setPeriod] = useState(7);
  const [customStart, setCustomStart] = useState(fmt(subDays(new Date(), 7)));
  const [customEnd, setCustomEnd] = useState(fmt(new Date()));
  const [loading, setLoading] = useState(false);
  const [atendentes, setAtendentes] = useState([]);
  const [sortBy, setSortBy] = useState("tickets");
  const [sortDir, setSortDir] = useState("desc");

  const getDateRange = () => {
    if (period === "custom") return { start: customStart, end: customEnd };
    const days = period === 0 ? 1 : period;
    return { start: fmt(subDays(new Date(), days)), end: fmt(new Date()) };
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const { data } = await api.get("/dashboard/ticketsUsers", {
        params: { dateStart: start, dateEnd: end, period }
      });
      setAtendentes(Array.isArray(data) ? data : data?.data || []);
    } catch {
      setAtendentes([]);
    }
    setLoading(false);
  }, [period, customStart, customEnd]);

  useEffect(() => { if (period !== "custom") loadData(); }, [period]);

  const handleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const sorted = [...atendentes].sort((a, b) => {
    let va = a[sortBy] ?? 0, vb = b[sortBy] ?? 0;
    if (typeof va === "string") { va = va.toLowerCase(); vb = String(vb).toLowerCase(); }
    if (va < vb) return sortDir === "asc" ? -1 : 1;
    if (va > vb) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  const total = atendentes.reduce((s, a) => s + (a.tickets || 0), 0);
  const totalRated = atendentes.filter(a => (a.ratings || 0) > 0);
  const avgRating = totalRated.length > 0
    ? totalRated.reduce((s, a) => s + (a.avgRating || 0), 0) / totalRated.length
    : 0;
  const topPerformer = [...atendentes].sort((a, b) => (b.tickets || 0) - (a.tickets || 0))[0];
  const podium = [...atendentes].sort((a, b) => (b.tickets || 0) - (a.tickets || 0)).slice(0, 3);

  const chartData = [...atendentes]
    .sort((a, b) => (b.tickets || 0) - (a.tickets || 0))
    .slice(0, 12)
    .map((a, i) => ({
      name: (a.name || "").split(" ")[0],
      value: a.tickets || 0,
      color: COLORS[i % COLORS.length]
    }));

  const getPeriodLabel = () => {
    if (period === "custom") return `${fmtDisplay(customStart)} a ${fmtDisplay(customEnd)}`;
    return PERIOD_OPTIONS.find(o => o.value === period)?.label || "Hoje";
  };

  const getRankBadge = (i) => {
    if (i === 0) return { bg: "#ffd700", color: "#744210" };
    if (i === 1) return { bg: "#c0c0c0", color: "#4a5568" };
    if (i === 2) return { bg: "#cd7f32", color: "#fff" };
    return { bg: "#e2e8f0", color: "#4a5568" };
  };

  const handleExport = () => {
    const now = format(new Date(), "dd/MM/yyyy HH:mm", { locale: ptBR });
    const rows = sorted.map((a, i) =>
      `<tr><td>${i + 1}</td><td>${a.name || ""}</td><td>${a.tickets || 0}</td><td>${Math.round(a.participation || 0)}%</td><td>${a.ratings || 0}</td><td>${(a.avgRating || 0).toFixed(1)}</td></tr>`
    ).join("");
    const html = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"/><title>Relatório Atendentes</title>
<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:-apple-system,sans-serif;padding:40px;background:#f5f5f5}
h1{font-size:26px;font-weight:800;margin-bottom:4px}p{color:#718096;margin-bottom:24px}
table{width:100%;border-collapse:collapse;background:#fff;border-radius:12px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,.07)}
th{background:#ed8936;color:#fff;padding:12px 16px;font-size:12px;text-transform:uppercase;letter-spacing:.5px;text-align:left}
td{padding:12px 16px;border-bottom:1px solid #f0f0f0;font-size:14px}
tr:last-child td{border:none}tr:hover td{background:#fff8f3}
.footer{margin-top:32px;color:#a0aec0;font-size:12px}</style></head>
<body><h1>👥 Relatório de Atendentes</h1><p>Período: ${getPeriodLabel()} · Exportado em ${now}</p>
<table><thead><tr><th>#</th><th>Atendente</th><th>Tickets</th><th>Participação</th><th>Avaliações</th><th>Nota Média</th></tr></thead>
<tbody>${rows}</tbody></table>
<div class="footer">Total: ${total} tickets · ${atendentes.length} atendentes</div></body></html>`;
    const blob = new Blob([html], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `relatorio-atendentes-${fmt(new Date())}.html`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box className={classes.root}>
      <Box className={classes.header}>
        <Box style={{ flex: "1 1 auto" }}>
          <Typography className={classes.title}>Relatório de Atendentes</Typography>
          <Typography className={classes.subtitle}>Desempenho, avaliações e participação por período</Typography>
        </Box>
        <Box className={classes.headerButtons}>
          <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={loadData} disabled={loading}
            style={{ borderRadius: 8, textTransform: "none" }}>
            Atualizar
          </Button>
          <Button variant="contained" startIcon={<GetAppIcon />} onClick={handleExport}
            disabled={loading || !atendentes.length}
            style={{ backgroundColor: "#ed8936", color: "#fff", borderRadius: 8, textTransform: "none" }}>
            Exportar HTML
          </Button>
        </Box>
      </Box>

      <Box className={classes.filterCard}>
        <FormControl size="small" variant="outlined" style={{ flex: "1 1 140px" }}>
          <InputLabel>Período</InputLabel>
          <Select value={period} onChange={e => setPeriod(e.target.value)} label="Período">
            {PERIOD_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </Select>
        </FormControl>
        {period === "custom" && (
          <>
            <TextField label="De" type="date" size="small" variant="outlined"
              value={customStart} onChange={e => setCustomStart(e.target.value)}
              InputLabelProps={{ shrink: true }} style={{ flex: "1 1 130px" }} />
            <TextField label="Até" type="date" size="small" variant="outlined"
              value={customEnd} onChange={e => setCustomEnd(e.target.value)}
              InputLabelProps={{ shrink: true }} style={{ flex: "1 1 130px" }} />
            <Button variant="contained" size="small" onClick={loadData}
              style={{ backgroundColor: "#ed8936", color: "#fff", textTransform: "none", borderRadius: 8 }}>
              Aplicar
            </Button>
          </>
        )}
        {loading && <CircularProgress size={20} />}
        {!loading && atendentes.length > 0 && (
          <Chip label={`${atendentes.length} atendentes · ${getPeriodLabel()}`} size="small"
            style={{ backgroundColor: "#fff3e5", color: "#c05621", fontWeight: 600 }} />
        )}
      </Box>

      <Typography className={classes.sectionLabel}>Visão Geral</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard} elevation={0}>
            <Box className={classes.metricInner} style={{ background: "linear-gradient(135deg,#fff3e5,#fbd38d)", borderTop: "3px solid #ed8936" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography className={classes.metricLabel} style={{ color: "#c05621" }}>Total de Tickets</Typography>
                <AssignmentTurnedInIcon style={{ color: "#ed8936", fontSize: 22, opacity: 0.8 }} />
              </Box>
              <Typography className={classes.metricValue} style={{ color: "#c05621" }}>{loading ? "—" : total}</Typography>
              <Typography className={classes.metricSub} style={{ color: "#c05621" }}>No período selecionado</Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard} elevation={0}>
            <Box className={classes.metricInner} style={{ background: "linear-gradient(135deg,#faf5ff,#e9d8fd)", borderTop: "3px solid #9f7aea" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography className={classes.metricLabel} style={{ color: "#6b46c1" }}>Atendentes Ativos</Typography>
                <PeopleIcon style={{ color: "#9f7aea", fontSize: 22, opacity: 0.8 }} />
              </Box>
              <Typography className={classes.metricValue} style={{ color: "#6b46c1" }}>{loading ? "—" : atendentes.length}</Typography>
              <Typography className={classes.metricSub} style={{ color: "#6b46c1" }}>Com tickets no período</Typography>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard} elevation={0}>
            <Box className={classes.metricInner} style={{ background: "linear-gradient(135deg,#fffff0,#fefcbf)", borderTop: "3px solid #d69e2e" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography className={classes.metricLabel} style={{ color: "#744210" }}>Avaliação Média</Typography>
                <StarIcon style={{ color: "#d69e2e", fontSize: 22, opacity: 0.8 }} />
              </Box>
              <Typography className={classes.metricValue} style={{ color: "#744210" }}>{loading ? "—" : avgRating.toFixed(1)}</Typography>
              <Box mt={0.5}><StarRating rating={avgRating} /></Box>
            </Box>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card className={classes.metricCard} elevation={0}>
            <Box className={classes.metricInner} style={{ background: "linear-gradient(135deg,#f0fff4,#c6f6d5)", borderTop: "3px solid #38a169" }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Typography className={classes.metricLabel} style={{ color: "#276749" }}>Melhor Atendente</Typography>
                <EmojiEventsIcon style={{ color: "#38a169", fontSize: 22, opacity: 0.8 }} />
              </Box>
              <Typography className={classes.metricValue} style={{ color: "#276749", fontSize: 22 }}>
                {loading || !topPerformer ? "—" : (topPerformer.name || "").split(" ")[0]}
              </Typography>
              <Typography className={classes.metricSub} style={{ color: "#276749" }}>
                {topPerformer ? `${topPerformer.tickets || 0} tickets` : "Sem dados"}
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>

      <Typography className={classes.sectionLabel}>Ranking por Volume</Typography>
      <Grid container spacing={2}>
        {podium.length > 0 && (
          <Grid item xs={12} md={4}>
            <Card elevation={0} className={classes.chartCard}>
              <Typography className={classes.chartTitle}>🏆 Top 3 Atendentes</Typography>
              {podium.map((a, i) => {
                const rank = getRankBadge(i);
                const initials = (a.name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
                return (
                  <Box key={a.id} display="flex" alignItems="center" style={{ gap: 12, padding: "10px 0", borderBottom: i < podium.length - 1 ? "1px solid #f0f0f0" : "none" }}>
                    <Box className={classes.rankBadge} style={{ background: rank.bg, color: rank.color, fontSize: 12 }}>
                      {i + 1}
                    </Box>
                    <Avatar style={{ width: 40, height: 40, fontSize: 15, fontWeight: 700, backgroundColor: COLORS[i] }}>
                      {initials}
                    </Avatar>
                    <Box flex={1}>
                      <Typography style={{ fontWeight: 700, fontSize: 14 }}>{a.name}</Typography>
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        <Typography style={{ fontSize: 12, color: "#718096" }}>{a.tickets || 0} tickets</Typography>
                        <Chip label={`${Math.round(a.participation || 0)}%`} size="small"
                          style={{ backgroundColor: COLORS[i] + "20", color: COLORS[i], fontWeight: 700, fontSize: 11, height: 20 }} />
                      </Box>
                      {(a.ratings || 0) > 0 && (
                        <Box display="flex" alignItems="center" style={{ gap: 4, marginTop: 2 }}>
                          <StarRating rating={a.avgRating || 0} />
                          <Typography style={{ fontSize: 11, color: "#a0aec0" }}>({a.ratings})</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Card>
          </Grid>
        )}

        <Grid item xs={12} md={podium.length > 0 ? 8 : 12}>
          <Card elevation={0} className={classes.chartCard}>
            <Typography className={classes.chartTitle}>Volume de Atendimentos por Agente</Typography>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={240}>
                <CircularProgress />
              </Box>
            ) : chartData.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" height={240}>
                <Typography style={{ color: "#a0aec0" }}>Sem dados no período</Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: "#a0aec0" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12, fill: "#718096" }} axisLine={false} tickLine={false} />
                  <ReTooltip content={<CustomBarTooltip />} cursor={{ fill: "rgba(0,0,0,0.04)" }} />
                  <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={18}>
                    {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </Card>
        </Grid>
      </Grid>

      <Typography className={classes.sectionLabel}>Detalhamento Individual</Typography>
      <Card elevation={0} className={classes.tableCard}>
        <TableContainer>
          <Table size="small">
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell className={classes.tableHeadCell} style={{ width: 50 }}>#</TableCell>
                <TableCell className={classes.tableHeadCell}>
                  <TableSortLabel active={sortBy === "name"} direction={sortBy === "name" ? sortDir : "asc"}
                    onClick={() => handleSort("name")} style={{ color: "#fff" }}>
                    Atendente
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell} align="center">
                  <TableSortLabel active={sortBy === "tickets"} direction={sortBy === "tickets" ? sortDir : "desc"}
                    onClick={() => handleSort("tickets")} style={{ color: "#fff" }}>
                    Tickets
                  </TableSortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Participação</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">Avaliações</TableCell>
                <TableCell className={classes.tableHeadCell} align="center">
                  <TableSortLabel active={sortBy === "avgRating"} direction={sortBy === "avgRating" ? sortDir : "desc"}
                    onClick={() => handleSort("avgRating")} style={{ color: "#fff" }}>
                    Nota Média
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ padding: 40 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ padding: 40, color: "#a0aec0" }}>
                    Nenhum dado encontrado para o período selecionado
                  </TableCell>
                </TableRow>
              ) : sorted.map((a, i) => {
                const rank = getRankBadge(i);
                const initials = (a.name || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
                const participation = Math.round(a.participation || 0);
                return (
                  <TableRow key={a.id} className={classes.tableRow}>
                    <TableCell>
                      <Box className={classes.rankBadge} style={{ background: rank.bg, color: rank.color, width: 24, height: 24, fontSize: 11 }}>
                        {i + 1}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center" style={{ gap: 10 }}>
                        <Avatar className={classes.avatar} style={{ backgroundColor: COLORS[i % COLORS.length] }}>
                          {initials}
                        </Avatar>
                        <Box>
                          <Typography style={{ fontWeight: 600, fontSize: 14 }}>{a.name}</Typography>
                          {a.email && <Typography style={{ fontSize: 11, color: "#a0aec0" }}>{a.email}</Typography>}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip label={a.tickets || 0} size="small"
                        style={{ backgroundColor: COLORS[i % COLORS.length] + "20", color: COLORS[i % COLORS.length], fontWeight: 700, minWidth: 44 }} />
                    </TableCell>
                    <TableCell align="center">
                      <Box>
                        <Typography style={{ fontSize: 14, fontWeight: 600 }}>{participation}%</Typography>
                        <Box style={{ height: 4, backgroundColor: "#e2e8f0", borderRadius: 2, marginTop: 3 }}>
                          <Box style={{ height: "100%", width: `${participation}%`, backgroundColor: "#ed8936", borderRadius: 2 }} />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography style={{ fontSize: 13, color: "#718096" }}>{a.ratings || 0}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      {(a.ratings || 0) > 0 ? (
                        <Tooltip title={`${(a.avgRating || 0).toFixed(2)} / 5`}>
                          <Box>
                            <Typography style={{ fontSize: 13, fontWeight: 700, color: "#d69e2e", marginBottom: 2 }}>
                              {(a.avgRating || 0).toFixed(1)}
                            </Typography>
                            <StarRating rating={a.avgRating || 0} />
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography style={{ fontSize: 12, color: "#cbd5e0" }}>—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}
